/**
 * 分镜检查器 - 「视频」tab
 *
 * 包含：
 *  - 时长（秒）
 *    时长按视频 adapter 声明的 supportedDurations 渲染：
 *      - enum：按钮组（用户只能选指定值）
 *      - range：滑块（min/max/step）
 *    无 spec 时回退到通用数字输入（保底）
 *  - 视频预览（已激活）
 *  - 激活控制深色面板（激活 / 重激活 / 取消）
 *  - 没锁 keyframe 时给出引导回「关键帧」tab 的提示
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Wand2,
  RefreshCw,
  X as XIcon,
  ImageOff,
  Clock,
  Check,
  Trash2,
  Play,
  History,
} from 'lucide-react';
import type { DurationSpec } from '../../../../../baselines/video/services/videoApi';
import {
  getDurationValuesFromSpec,
  isDurationInSpec,
} from '../../../../../baselines/video/hooks/useVideoModelCapabilities';
import toast from '../../../../../components/Toast';
import { formatBeijingDate } from '../../../../../utils/time';
import { resolveBrowserMediaUrl } from '../../../../../utils/storageUrl';
import {
  directorApi,
  DirectorApiError,
  type DirectorShotMode,
} from '../../../services/directorApi';

type ActivationStatus =
  | 'none'
  | 'queued'
  | 'pending'
  | 'submitting'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'timeout';

const SECTION_LABEL =
  'flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500';
const FIELD =
  'w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100';

interface CandidateVideoItem {
  id: string;
  videoUrl: string | null;
  coverUrl: string | null;
  resultDuration: number | null;
  duration: number;
  createdAt: string;
}

interface Props {
  /** 当前分镜的视频参考素材是否已就绪 */
  hasVisualReference: boolean;
  /** 分镜模式（决定缺失引导文案与按钮文案） */
  shotMode: DirectorShotMode;
  /** 分镜 ID（用于切换 / 删除候选时调 API） */
  shotId: string;
  activationStatus: ActivationStatus;
  /** 视频成片 URL（completed 时显示预览） */
  videoUrl: string | null;
  /** 时长受控字段 */
  duration: number | null;
  /** 是否禁用时长编辑（视频已激活 / 进行中时禁用） */
  videoActive: boolean;
  setDuration: (v: number | null) => void;
  /** 项目所选视频模型在 img2video 模式下的时长能力声明（按 adapter） */
  videoDurationSpec: DurationSpec | null;
  /** 视频模型默认时长（占位提示用） */
  videoDurationDefault: number | null;
  /** 历史候选视频列表（按 createdAt desc） */
  candidateVideos: CandidateVideoItem[];
  /** 切换 active / 删除候选成功后通知外层 reload 项目 */
  onShotUpdated: () => void;

  onRequestActivate: () => void;
  onRequestReactivate: () => void;
  onRequestDeactivate: () => void;
  /** 让用户跳到素材准备 tab */
  onGoToVisualTab: () => void;
}

export function ShotVideoTab({
  hasVisualReference,
  shotMode,
  shotId,
  activationStatus,
  videoUrl,
  duration,
  videoActive,
  setDuration,
  videoDurationSpec,
  videoDurationDefault,
  candidateVideos,
  onShotUpdated,
  onRequestActivate,
  onRequestReactivate,
  onRequestDeactivate,
  onGoToVisualTab,
}: Props) {
  const isCompleted = activationStatus === 'completed';
  const isInProgress =
    activationStatus === 'queued' ||
    activationStatus === 'pending' ||
    activationStatus === 'submitting' ||
    activationStatus === 'processing';
  const isFailed = activationStatus === 'failed' || activationStatus === 'timeout';

  // 当 spec 已知且当前 duration 不在合法集合内 → 自动 clamp 到默认值或第一个合法值
  // （avoids saving invalid values that would be rejected on activate）
  useEffect(() => {
    if (videoActive) return;
    if (!videoDurationSpec) return;
    if (duration === null) return; // 留空表示"使用模型默认"，无需自动改
    if (isDurationInSpec(videoDurationSpec, duration)) return;
    const fallback =
      (videoDurationDefault &&
      isDurationInSpec(videoDurationSpec, videoDurationDefault)
        ? videoDurationDefault
        : null) ?? getDurationValuesFromSpec(videoDurationSpec)[0] ?? null;
    if (fallback !== null) setDuration(fallback);
  }, [videoDurationSpec, videoDurationDefault, duration, videoActive, setDuration]);

  const visualReferenceCopy = useMemo(() => {
    if (shotMode === 'frame_sequence') {
      return {
        title: '还没有可用的帧序列',
        description:
          '视频激活会使用帧序列中的参考图。先去「多图 → 视频」tab 添加至少 1 张可用帧。',
        cta: '去准备帧序列',
      };
    }
    if (shotMode === 'trinity_cards') {
      return {
        title: '三合一卡还没准备完整',
        description:
          '视频激活固定需要 3 张参考卡。先去「三合一参考」tab 生成角色卡、故事卡和视频卡。',
        cta: '去准备三合一卡',
      };
    }
    return {
      title: '还没有锁定的关键帧',
      description:
        '视频激活会以锁定的关键帧作为首帧。先去「关键帧」tab 生成或导入一张并锁定。',
      cta: '去选关键帧',
    };
  }, [shotMode]);

  const activationPanelLabel =
    shotMode === 'frame_sequence'
      ? '帧序列激活'
      : shotMode === 'trinity_cards'
        ? '三合一激活'
        : '视频激活';

  const activationButtonLabel =
    shotMode === 'frame_sequence'
      ? '激活帧序列'
      : shotMode === 'trinity_cards'
        ? '激活三合一卡'
        : '激活视频';

  const activationAccentClass =
    shotMode === 'frame_sequence'
      ? 'text-amber-200'
      : shotMode === 'trinity_cards'
        ? 'text-violet-200'
        : 'text-sky-200';

  // 时长的编辑区（无论是否锁了 keyframe 都允许配置）
  const renderDurationField = () => {
    // 1. 无 spec：保底数字输入
    if (!videoDurationSpec) {
      return (
        <input
          type="number"
          value={duration ?? ''}
          onChange={(e) => setDuration(e.target.value ? parseInt(e.target.value, 10) : null)}
          min={1}
          max={30}
          placeholder={videoDurationDefault ? `模型默认 ${videoDurationDefault}s` : '模型默认'}
          disabled={videoActive}
          className={`${FIELD} disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400`}
        />
      );
    }

    // 2. enum：按钮组
    if (videoDurationSpec.type === 'enum') {
      const values = videoDurationSpec.values;
      if (values.length === 0) {
        return (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-600">
            视频模型未声明可用时长，请联系管理员检查 adapter 配置
          </div>
        );
      }
      // 当前值的"显示"：未设置时回退到模型默认
      const current = duration ?? videoDurationDefault ?? values[0];
      return (
        <div className="flex flex-wrap gap-1.5">
          {values.map((sec) => {
            const active = sec === current;
            return (
              <button
                key={sec}
                type="button"
                onClick={() => setDuration(sec)}
                disabled={videoActive}
                className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  active
                    ? 'border-sky-500 bg-sky-500 text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-sky-300'
                }`}
              >
                {sec}s
              </button>
            );
          })}
        </div>
      );
    }

    // 3. range：滑块 + 数字
    const { min, max, step } = videoDurationSpec;
    const current = duration ?? videoDurationDefault ?? min;
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-3">
          <span className="w-8 text-right text-[11px] text-slate-400">{min}s</span>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={current}
            onChange={(e) => setDuration(parseInt(e.target.value, 10))}
            disabled={videoActive}
            className="flex-1 accent-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <span className="w-8 text-[11px] text-slate-400">{max}s</span>
          <span className="rounded-lg bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">
            {current}s
          </span>
        </div>
        <p className="text-[10px] text-slate-400">
          可选范围 {min}–{max}s，步长 {step}s
        </p>
      </div>
    );
  };

  const renderParams = () => (
    <div className="grid grid-cols-1 gap-3">
      <div className="space-y-1.5">
        <label className={SECTION_LABEL}>
          <Clock className="h-3 w-3" /> 时长
          {videoDurationSpec && (
            <span className="ml-1 normal-case tracking-normal text-[10px] font-normal text-slate-400">
              （按视频模型能力）
            </span>
          )}
        </label>
        {renderDurationField()}
      </div>
    </div>
  );

  // 缺少视频参考素材 → 显示参数 + 引导
  if (!hasVisualReference) {
    return (
      <div className="space-y-4">
        {renderParams()}
        {/* 即使没有锁定 keyframe，候选池里可能有历史视频（用户可恢复任意一条为 active） */}
        {candidateVideos.length > 0 && (
          <CandidateVideoStrip
            shotId={shotId}
            candidates={candidateVideos}
            onUpdated={onShotUpdated}
          />
        )}
        <div className="space-y-3 rounded-2xl border border-dashed border-slate-300 bg-white/40 p-5 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
            <ImageOff className="h-5 w-5" />
          </div>
          <p className="text-sm font-medium text-slate-700">{visualReferenceCopy.title}</p>
          <p className="text-xs leading-5 text-slate-500">{visualReferenceCopy.description}</p>
          <button
            type="button"
            onClick={onGoToVisualTab}
            className="inline-flex items-center justify-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700"
          >
            {visualReferenceCopy.cta}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 时长 / 衔接 */}
      {renderParams()}

      {/* 已激活：视频预览 */}
      {isCompleted && videoUrl && (
        <div className="overflow-hidden rounded-2xl bg-slate-950 shadow-md">
          <video src={resolveBrowserMediaUrl(videoUrl)} controls playsInline className="aspect-video w-full bg-black" />
        </div>
      )}

      {/* 历史候选视频条（取消激活的 completed 视频会自动入候选） */}
      {candidateVideos.length > 0 && (
        <CandidateVideoStrip
          shotId={shotId}
          candidates={candidateVideos}
          onUpdated={onShotUpdated}
        />
      )}

      {/* 激活控制深色面板 */}
      <div className="space-y-3 rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-4 text-white shadow-lg shadow-slate-300/50">
        <h4 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-300">
          <Wand2 className={`h-3 w-3 ${activationAccentClass}`} /> {activationPanelLabel}
        </h4>

        {isCompleted ? (
          <div className="space-y-2">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-2.5 py-1 text-[11px] font-medium text-emerald-200">
              视频已激活
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onRequestReactivate}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
              >
                <RefreshCw className="h-3.5 w-3.5" /> 重新激活
              </button>
              <button
                type="button"
                onClick={onRequestDeactivate}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
              >
                <XIcon className="h-3.5 w-3.5" /> 取消
              </button>
            </div>
          </div>
        ) : isInProgress ? (
          <div className="space-y-2">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-amber-300/15 px-2.5 py-1 text-[11px] font-medium text-amber-100">
              {activationStatus === 'queued' ? '排队中…' : '生成中…'}
            </p>
            {(activationStatus === 'queued' || activationStatus === 'pending') && (
              <button
                type="button"
                onClick={onRequestDeactivate}
                className="w-full rounded-2xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
              >
                取消任务
              </button>
            )}
          </div>
        ) : isFailed ? (
          <div className="space-y-2">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-rose-400/15 px-2.5 py-1 text-[11px] font-medium text-rose-200">
              {activationStatus === 'timeout'
                ? '上次激活超时，已退还积分'
                : '上次激活失败，已退还积分'}
            </p>
            <button
              type="button"
              onClick={onRequestActivate}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-2xl bg-white px-3 py-2 text-sm font-semibold text-slate-950 transition hover:scale-[1.02] hover:bg-sky-50"
            >
              <RefreshCw className="h-4 w-4" /> 重新激活
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onRequestActivate}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-2xl bg-white px-3 py-2.5 text-sm font-semibold text-slate-950 transition hover:scale-[1.02] hover:bg-sky-50"
          >
            <Wand2 className="h-4 w-4" /> {activationButtonLabel}
          </button>
        )}
      </div>
    </div>
  );
}


// ---------------- 候选视频缩略图条 ----------------

interface CandidateVideoStripProps {
  shotId: string;
  candidates: CandidateVideoItem[];
  onUpdated: () => void;
}

/**
 * 历史候选视频缩略图条。
 *
 * 行为：
 * - 缩略图 = 视频封面；hover 时静音播放
 * - "使用此版本" → 切换为 active（不扣费，原 active 自动变候选）
 * - "删除" → 软删（isHidden=true，留 7 天反悔窗口；后台 worker 兜底清理 storage）
 *
 * 候选数量上限由后端维护（默认 5 条），超出时最旧的会被自动软删，前端只展示。
 */
function CandidateVideoStrip({
  shotId,
  candidates,
  onUpdated,
}: CandidateVideoStripProps) {
  const [busyId, setBusyId] = useState<string | null>(null);

  const handleUse = async (videoJobId: string) => {
    if (busyId) return;
    setBusyId(videoJobId);
    try {
      await directorApi.useVideoCandidate(shotId, videoJobId);
      toast.success('已切换为当前视频');
      onUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '切换失败');
    } finally {
      setBusyId(null);
    }
  };

  const handleHide = async (videoJobId: string) => {
    if (busyId) return;
    if (!window.confirm('确定删除这个候选视频？删除后默认保留 7 天可恢复。')) {
      return;
    }
    setBusyId(videoJobId);
    try {
      await directorApi.hideVideoCandidate(shotId, videoJobId);
      toast.success('已删除');
      onUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '删除失败');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
          <History className="h-3 w-3 text-slate-400" />
          历史候选（{candidates.length}）
        </h4>
        <span className="text-[10px] text-slate-400">
          取消激活的视频自动保留为候选
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {candidates.map((c) => (
          <CandidateThumb
            key={c.id}
            candidate={c}
            busy={busyId === c.id}
            onUse={() => handleUse(c.id)}
            onHide={() => handleHide(c.id)}
          />
        ))}
      </div>
    </div>
  );
}

function CandidateThumb({
  candidate,
  busy,
  onUse,
  onHide,
}: {
  candidate: CandidateVideoItem;
  busy: boolean;
  onUse: () => void;
  onHide: () => void;
}) {
  const [hovering, setHovering] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const onMouseEnter = () => {
    setHovering(true);
    if (candidate.videoUrl && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => undefined);
    }
  };
  const onMouseLeave = () => {
    setHovering(false);
    videoRef.current?.pause();
  };

  // 时长标签：优先 resultDuration（实际时长，浮点），fallback duration（用户设置，整数）
  const durationLabel =
    candidate.resultDuration !== null
      ? `${Math.round(candidate.resultDuration)}s`
      : `${candidate.duration}s`;

  // 创建时间相对描述
  const createdLabel = relativeTime(candidate.createdAt);

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`group relative aspect-video overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200 transition ${
        busy ? 'opacity-50' : 'hover:ring-sky-300'
      }`}
    >
      {/* 封面 */}
      {candidate.coverUrl ? (
        <img
          src={resolveBrowserMediaUrl(candidate.coverUrl)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-400">
          <Play className="h-5 w-5" />
        </div>
      )}
      {/* hover 时叠加视频 */}
      {candidate.videoUrl && (
        <video
          ref={videoRef}
          src={resolveBrowserMediaUrl(candidate.videoUrl)}
          muted
          loop
          playsInline
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${hovering ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
      {/* 顶部信息 */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between gap-1 bg-gradient-to-b from-black/60 to-transparent px-1.5 py-1 text-[10px] text-white/90">
        <span>{createdLabel}</span>
        <span className="rounded-md bg-black/45 px-1 py-0.5">{durationLabel}</span>
      </div>
      {/* 底部操作 */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-gradient-to-t from-black/70 to-transparent px-1 py-1 opacity-0 transition group-hover:opacity-100">
        <button
          type="button"
          disabled={busy}
          onClick={onUse}
          className="rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-semibold text-slate-900 shadow transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          title="使用此版本作为当前激活视频"
        >
          <Check className="-mt-px mr-0.5 inline h-3 w-3" />
          使用
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onHide}
          className="rounded-full bg-white/95 p-1 text-rose-600 shadow transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
          title="删除该候选（保留 7 天可恢复）"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  return formatBeijingDate(iso);
}
